import { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { Check, Copy } from "lucide-react";
import { cn } from "@/lib/cn";
import { getHighlighter, normalizeLang, preferredTheme } from "@/lib/shiki";

interface CodeBlockProps {
  code: string;
  language?: string;
}

export function CodeBlock({ code, language }: CodeBlockProps) {
  const [html, setHtml] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const lang = normalizeLang(language);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const highlighter = await getHighlighter();
        const rendered = highlighter.codeToHtml(code, {
          lang,
          theme: preferredTheme(),
        });
        if (!cancelled) setHtml(DOMPurify.sanitize(rendered));
      } catch {
        if (!cancelled) setHtml(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, lang]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard may be unavailable; ignore */
    }
  };

  return (
    <div className="ndc-code group relative my-3 overflow-hidden rounded-[10px] border border-border bg-surface-muted">
      <div className="flex items-center justify-between border-b border-border px-3 py-1.5 text-[11px] uppercase tracking-wide text-text-muted">
        <span>{lang === "text" ? "code" : lang}</span>
        <button
          type="button"
          onClick={handleCopy}
          aria-label="Copy code"
          className={cn(
            "inline-flex items-center gap-1 rounded-md border border-transparent px-1.5 py-0.5 text-[11px] text-text-muted transition-colors hover:border-border hover:bg-surface hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
            copied && "text-[var(--success)] hover:text-[var(--success)]",
          )}
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5" />
              Copied
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" />
              Copy
            </>
          )}
        </button>
      </div>
      {html ? (
        <div
          className="ndc-code-shiki overflow-x-auto text-[13px] leading-relaxed"
          // Shiki output is HTML; we run it through DOMPurify before injection.
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <pre className="overflow-x-auto px-3 py-2 text-[13px] leading-relaxed">
          <code>{code}</code>
        </pre>
      )}
    </div>
  );
}
